import { money } from '@/utils/money'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300
const MAX_RESULTS = 3

type SuggestedProduct = {
	id: number
	title: string
	url: string
	price: string
	image?: string
	featured_image?: { url?: string; alt?: string }
	available?: boolean
}

type PredictiveSearchConfig = {
	searchUrl?: string
	suggestUrl?: string
}

export default (config: PredictiveSearchConfig = {}) => ({
	searchUrl: config.searchUrl || '/search',
	suggestUrl: config.suggestUrl || '/search/suggest.json',

	query: '',
	results: [] as SuggestedProduct[],
	loading: false,
	fetched: false,
	panelOpen: false,

	// Desktop-only expand/collapse state (unused by the mobile bar)
	searchOpen: false,

	debounceTimer: null as ReturnType<typeof setTimeout> | null,
	controller: null as AbortController | null,

	init() {
		this.$watch('query', () => this.onQueryChange())
	},

	get showPanel() {
		return this.panelOpen && this.fetched
	},

	get hasResults() {
		return this.results.length > 0
	},

	get noResults() {
		return this.fetched && !this.loading && this.results.length === 0
	},

	get statusText() {
		if (!this.showPanel) return ''
		return this.hasResults
			? `${this.results.length} product suggestion${this.results.length === 1 ? '' : 's'} available.`
			: 'No product suggestions found.'
	},

	onQueryChange() {
		const q = this.query.trim()

		if (q.length < MIN_QUERY_LENGTH) {
			this.cancelPending()
			this.loading = false
			this.fetched = false
			this.panelOpen = false
			return
		}

		this.loading = true

		if (this.debounceTimer) clearTimeout(this.debounceTimer)
		this.debounceTimer = setTimeout(() => this.fetchResults(q), DEBOUNCE_MS)
	},

	async fetchResults(q: string) {
		this.controller?.abort()
		const controller = new AbortController()
		this.controller = controller

		const params = new URLSearchParams({
			q,
			'resources[type]': 'product',
			'resources[limit]': String(MAX_RESULTS),
			'resources[options][unavailable_products]': 'last',
		})

		try {
			const res = await fetch(`${this.suggestUrl}?${params.toString()}`, {
				signal: controller.signal,
			})
			if (!res.ok) throw new Error(`Predictive search failed (${res.status})`)

			const data = await res.json()
			if (controller.signal.aborted) return

			this.results = (data?.resources?.results?.products ?? []).slice(0, MAX_RESULTS)
			this.fetched = true
			this.panelOpen = true
			this.loading = false
		} catch (error: any) {
			if (error?.name === 'AbortError') return

			// Fail quietly — the user can still submit the full search
			this.loading = false
			this.fetched = false
			this.panelOpen = false
		}
	},

	cancelPending() {
		if (this.debounceTimer) clearTimeout(this.debounceTimer)
		this.debounceTimer = null
		this.controller?.abort()
		this.controller = null
	},

	closePanel() {
		this.panelOpen = false
	},

	// Re-open cached results when the input regains focus
	openPanelIfCached() {
		if (this.fetched && this.query.trim().length >= MIN_QUERY_LENGTH) {
			this.panelOpen = true
		}
	},

	clear() {
		this.cancelPending()
		this.query = ''
		this.results = []
		this.loading = false
		this.fetched = false
		this.panelOpen = false
	},

	// Clicking a suggestion: let the link navigate, then reset the search UI.
	// Deferred so clearing the list never removes the clicked link mid-click.
	select() {
		setTimeout(() => {
			this.clear()
			this.searchOpen = false
			;(window.Alpine?.store('site') as any)?.closeMobileSearch?.()
		}, 0)
	},

	// "View All" and pressing Enter are equivalent
	submitSearch() {
		const q = this.query.trim()
		if (!q) return
		window.location.href = `${this.searchUrl}?q=${encodeURIComponent(q)}`
	},

	openSearch() {
		this.searchOpen = true
		this.$nextTick(() => {
			this.$refs.searchInput?.focus()
		})
	},

	closeSearch() {
		if (!this.$refs.searchInput?.value) {
			this.searchOpen = false
			this.panelOpen = false
		}
	},

	// Rendered box is 128x160 (4:5) — ask the CDN for that exact crop
	// so we never ship wasted pixels or crop client-side.
	thumbnail(product: SuggestedProduct, scale = 1) {
		const url = product?.featured_image?.url || product?.image
		if (!url) return ''
		const separator = url.includes('?') ? '&' : '?'
		return `${url}${separator}width=${128 * scale}&height=${160 * scale}&crop=center`
	},

	thumbnailSrcset(product: SuggestedProduct) {
		const base = this.thumbnail(product, 1)
		if (!base) return ''
		return `${base} 1x, ${this.thumbnail(product, 2)} 2x`
	},

	formatPrice(price: string | number) {
		const dollars = parseFloat(String(price))
		if (Number.isNaN(dollars)) return ''
		const cents = Math.round(dollars * 100)
		const currency = (window.Alpine?.store('cart') as any)?.currency || 'USD'
		return money(cents, cents % 100 !== 0, currency)
	},

	destroy() {
		this.cancelPending()
	},
})
